import {
  ActionError,
  BlockTitle,
  Button,
  ConfirmDialog,
  EmptyState,
  ListRow,
  ListRows,
  Skeleton,
  Surface,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { LoadError } from '@/app/load-error'
import { AssistantGuide } from '@/features/integrations/assistant-guide'
import { errorMessage } from '@/lib/errors'
import { useConnections, useDisconnect, type Connection } from './api'
import { formatDateTime } from './labels'

const introClass = 'font-body text-secondary max-w-[640px] text-[13px] leading-[1.6] font-light'

/**
 * Aba Aplicativos conectados (RN-AUT-24, P-AUT-27): os clientes MCP que a pessoa autorizou, com
 * "Desconectar" dizendo o efeito antes, e, desde a T53, o guia "Conectar um assistente" para todo
 * perfil (RN-INT-21). Conectar não acontece aqui: é o aplicativo que pede, e a pessoa consente em
 * `/oauth/consent`.
 */
export function ConnectionsTab() {
  const connections = useConnections()
  const disconnect = useDisconnect()
  const [confirming, setConfirming] = useState<Connection | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const items = connections.data ?? []

  function confirmDisconnect() {
    if (!confirming) return
    const name = confirming.oauthClient.name
    disconnect.mutate(confirming.oauthClient.id, {
      onSuccess: () => {
        setConfirming(null)
        setFailure(null)
        toast(`${name} desconectado`)
      },
      onError: (error) => {
        setConfirming(null)
        setFailure(errorMessage(error))
      },
    })
  }

  return (
    <div className="flex max-w-[1120px] flex-col gap-4">
      <p className={introClass}>
        Aplicativos que você autorizou a usar a Westhetics com o seu perfil. Cada um faz o que você
        faz pelo assistente: busca e cadastra pacientes, manda anamnese, gera e envia contratos.
        Nada de administrar a clínica. A conexão vale 12 horas de cada vez; desconectar corta o
        acesso na hora.
      </p>

      {failure ? (
        <ActionError
          title="Não foi possível desconectar"
          description={failure}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      {connections.isError ? (
        <LoadError
          title="Não conseguimos carregar seus aplicativos"
          error={connections.error}
          onRetry={() => void connections.refetch()}
        />
      ) : connections.isPending ? (
        <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
          <Skeleton className="h-[64px] w-full" />
          <Skeleton className="h-[64px] w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum aplicativo conectado"
          description="Quando você conectar o Claude, o ChatGPT ou outro assistente à sua conta, ele aparece aqui, e é aqui que você desfaz a conexão. O passo a passo está logo abaixo."
        />
      ) : (
        <ListRows>
          {items.map((connection) => (
            <ListRow
              key={connection.oauthClient.id}
              title={connection.oauthClient.name}
              meta={connectionMeta(connection)}
              trailing={
                <Button variant="outline" size="sm" onClick={() => setConfirming(connection)}>
                  Desconectar
                </Button>
              }
            />
          ))}
        </ListRows>
      )}

      <Surface
        role="region"
        className="max-desk:px-4 mt-4"
        aria-labelledby="connect-assistant-title"
      >
        <BlockTitle title={<span id="connect-assistant-title">Conectar um assistente</span>} />
        <div className="mt-4">
          <AssistantGuide />
        </div>
      </Surface>

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null)
        }}
        title={`Desconectar o ${confirming?.oauthClient.name ?? 'aplicativo'}?`}
        effect="O aplicativo perde o acesso agora: o token dele deixa de valer e a próxima chamada é recusada. Nada do que ele consultou é apagado, e você pode conectar de novo pelo próprio aplicativo quando quiser."
        confirmLabel="Desconectar"
        loading={disconnect.isPending}
        loadingLabel="Desconectando…"
        onConfirm={confirmDisconnect}
      />
    </div>
  )
}

/** "conectado em 28/09/2026 às 12:03 · https://claude.ai/code". */
function connectionMeta(connection: Connection) {
  const parts: string[] = []
  if (connection.authorizedAt) {
    parts.push(`conectado em ${formatDateTime(new Date(connection.authorizedAt))}`)
  }
  if (connection.oauthClient.website) parts.push(connection.oauthClient.website)
  return parts.join(' · ')
}
